/**
 * The chassis every creator screen sits in: soft background, a header with back /
 * four-segment progress / close, the screen's own content, and a fixed footer for
 * the primary action.
 *
 * Identical on every theme — labels, actions, step names and the progress bar
 * never change with the vibe (decision D4).
 */
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { C, FONT, GRADIENT, SHADOW, TINT } from "./tokens";
import { isDarkPalette, paletteVars } from "./palettes";

const SEGMENT_KEYS = ["start", "clues", "reveal", "send"];

// The full visible height. Only `dvh` is safe: on iOS Safari `100vh` is the
// height with the toolbar hidden, taller than the screen while it shows, so a
// 100vh floor made the whole page scroll and pushed the header (back, close)
// off the top. Browsers without `dvh` get 100vh.
const FULL_HEIGHT =
  typeof CSS !== "undefined" && CSS.supports && CSS.supports("height", "100dvh") ? "100dvh" : "100vh";

function CircleButton({ label, icon, onClick, dark }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        width: 44, height: 44, borderRadius: 999, border: dark ? `1.5px solid ${C.dash}` : "none", background: C.surface, boxSizing: "border-box",
        boxShadow: SHADOW.raised, display: "grid", placeItems: "center", cursor: "pointer", padding: 0,
      }}
    >
      <Icon name={icon} size={icon === "close" ? 16 : 18} color={C.ink} strokeWidth={2.4} />
    </button>
  );
}

export function StepProgress({ segment }) {
  const { t: tr } = useTranslation();
  return (
    <div
      role="group"
      aria-label={tr("app.creator.progress.label", { step: tr(`app.creator.progress.${SEGMENT_KEYS[segment]}`), total: 4 })}
      style={{
        display: "flex", gap: 5, padding: "9px 13px", borderRadius: 999,
        background: TINT.glass(0.8), boxShadow: `0 6px 18px -8px ${TINT.shadow(0.2)}`,
      }}
    >
      {SEGMENT_KEYS.map((key, i) => (
        <span
          key={key}
          style={{ width: 26, height: 5, borderRadius: 3, background: i <= segment ? C.accent : C.track }}
        />
      ))}
    </div>
  );
}

// `height` defaults to the viewport; the landing page passes a pixel height to
// show real creator screens inside its phone mockups. `theme` tints the whole
// chassis with that vibe's palette (palettes.js); without one it is Puzzly's own.
export default function CreatorShell({ segment = 0, onBack, onClose, footer, children, scroll = true, height = "100dvh", theme = null, overlay = null }) {
  const { t: tr } = useTranslation();
  const dark = isDarkPalette(theme);
  return (
    <div
      data-testid="creator-shell"
      data-theme={theme || "house"}
      style={{
        ...paletteVars(theme),
        // A fixed height, not a minimum: with minHeight the shell grows past the
        // viewport when a screen is tall, main never scrolls on its own and the
        // footer slides below the fold (caught in Italian, where copy is longer).
        height: height === "100dvh" ? FULL_HEIGHT : height, boxSizing: "border-box", position: "relative", overflow: "hidden",
        background: GRADIENT.page, color: C.ink, fontFamily: FONT,
        display: "flex", flexDirection: "column",
      }}
    >
      <div aria-hidden="true" style={{ position: "absolute", top: -110, left: -120, width: 340, height: 340, borderRadius: 999, background: C.blob1, filter: "blur(64px)", opacity: 0.85 }} />
      <div aria-hidden="true" style={{ position: "absolute", top: 320, right: -150, width: 320, height: 320, borderRadius: 999, background: C.blob2, filter: "blur(70px)", opacity: 0.8 }} />

      {/* Nell'app la pagina va sotto la barra di stato (viewport-fit=cover): la testata
          parte dopo l'area sicura, o ora e batteria coprono i pulsanti (iPhone, 21/10). */}
      <header style={{ position: "relative", zIndex: 2, height: "calc(env(safe-area-inset-top, 0px) + 64px)", boxSizing: "border-box", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", paddingTop: "env(safe-area-inset-top, 0px)" }}>
        {onBack ? <CircleButton label={tr("app.creator.nav.back")} icon="back" onClick={onBack} dark={dark} /> : <span style={{ width: 44 }} />}
        <StepProgress segment={segment} />
        {onClose ? <CircleButton label={tr("app.creator.nav.close")} icon="close" onClick={onClose} dark={dark} /> : <span style={{ width: 44 }} />}
      </header>

      <main
        style={{
          position: "relative", zIndex: 1, flexGrow: 1, padding: "16px 24px 0",
          overflowY: scroll ? "auto" : "hidden", WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </main>

      {footer && (
        <div style={{ position: "relative", zIndex: 2, flexShrink: 0, padding: "12px 20px calc(env(safe-area-inset-bottom, 0px) + 24px)", display: "flex", flexDirection: "column", gap: 10 }}>
          {/* A soft fade so content scrolling under the footer reads as scroll
              rather than as something cut off. */}
          <span aria-hidden="true" style={{ position: "absolute", left: 0, right: 0, top: -28, height: 28, background: `linear-gradient(180deg, ${TINT.fade(0)} 0%, ${TINT.fade(0.92)} 100%)`, pointerEvents: "none" }} />
          {footer}
        </div>
      )}
      {overlay}
    </div>
  );
}
